import { Suspense } from "react";

import QuoteApp from "../components/quote/QuoteApp";
import { QuoteProvider } from "../components/quote/QuoteProvider";
import { ConfigProvider, Spin } from "antd";
import { useRoutes, useSearchParams } from "react-router-dom";
// @ts-ignore - useTranslation with namespace parameter
import { useTranslation } from "react-i18next";
import "../components/quote/QuoteApp.scss";
import { PatientAuthQuery } from "components/patient-portal/authentication/PatientAuthQuery";
import { MobileBreakpointProvider } from "../components/quote/MobileBreakpointContext";
import logo from "../img/logo-tile.svg";

/**
 * Fallback for the Suspense boundary that wraps the whole funnel. QuoteProvider
 * opens with a `useLazyLoadQuery`, so this boundary suspends on EVERY first
 * paint — and until 2026-09-13 it had no fallback at all, which renders as a
 * blank white page for the length of that query.
 *
 * ZERO data dependencies on purpose: a bundled SVG, a spinner and one bundled
 * string. It must be able to paint before anything has loaded. Mirrors
 * TodoLoadingShell / SignerLoadingShell (gray 100vh ground, one line of
 * plain-language label — a silent spinner reads as broken). It renders OUTSIDE
 * the ConfigProvider below, so it avoids any theme-token dependency too.
 */
const FunnelLoadingShell = () => {
  // @ts-ignore - useTranslation with namespace parameter
  const { t } = useTranslation("quote");
  return (
    <div
      style={{
        minHeight: "100vh",
        backgroundColor: "#f5f5f5",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexDirection: "column",
        gap: 16,
      }}
    >
      <img src={logo} alt="" style={{ height: 44, width: "auto" }} />
      <Spin size="large" />
      <div style={{ color: "#666", fontSize: 14 }}>
        {t("loading.funnel", {
          defaultValue: "Getting your free evaluation ready…",
        })}
      </div>
    </div>
  );
};

const Quote = () => {
  const [searchParams] = useSearchParams();

  const selectedSurgeon = searchParams.get("selected_surgeon");
  const selectedLocation = searchParams.get("selected_location");
  const selectedProcedures = searchParams.getAll("selected_procedures[]");
  const initialGiftCode = searchParams.get("gift") || undefined;

  let routes = useRoutes([
    { path: "/", element: <QuoteApp /> },
    { path: "select-location", element: <QuoteApp /> },
    { path: "select-procedure", element: <QuoteApp /> },
    { path: "preview-range", element: <QuoteApp /> },
    { path: "contact", element: <QuoteApp /> },
    { path: "select-surgeon", element: <QuoteApp /> },
    { path: "photos", element: <QuoteApp /> },
    { path: "estimate", element: <QuoteApp /> },
    { path: "book-now", element: <QuoteApp /> },
    { path: "*", element: <QuoteApp /> },
  ]);

  return (
    <Suspense fallback={<FunnelLoadingShell />}>
      <ConfigProvider
        theme={{
          token: {
            colorPrimary: "#111827",
            colorLink: "#111827",
            colorInfo: "#111827",
            colorBorder: "#e5e5e5",
            colorText: "#171717",
            colorTextSecondary: "#525252",
            fontSize: 16,
            borderRadius: 10,
            fontFamily:
              "system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
          },
          components: {
            Button: {
              borderRadius: 10,
              controlHeight: 44,
              fontWeight: 600,
            },
            Input: {
              borderRadius: 10,
              controlHeight: 44,
            },
            Select: {
              borderRadius: 10,
              controlHeight: 44,
              optionSelectedBg: "#f5f5f5",
              optionSelectedColor: "#171717",
              optionSelectedFontWeight: 600,
              optionActiveBg: "#fafafa",
            },
            DatePicker: {
              borderRadius: 10,
              controlHeight: 44,
            },
            Card: {
              borderRadiusLG: 14,
            },
            Modal: {
              borderRadiusLG: 14,
            },
          },
        }}
      >
        <MobileBreakpointProvider>
          <PatientAuthQuery>
            <QuoteProvider
              initialSelectedProcedures={selectedProcedures}
              initialSelectedSurgeon={selectedSurgeon}
              initialSelectedLocation={selectedLocation}
              initialGiftCode={initialGiftCode}
            >
              <div className="quoteContainer">{routes}</div>
            </QuoteProvider>
          </PatientAuthQuery>
        </MobileBreakpointProvider>
      </ConfigProvider>
    </Suspense>
  );
};

export default Quote;
